import React from "react";
import { TrendingUp, Clock, Users } from "lucide-react";

const CASE_STUDIES = [
  {
    id: 1,
    business: "Brightsmile Dental",
    industry: "Healthcare",
    challenge: "Front desk overwhelmed with after-hours booking calls. Patients gave up and went elsewhere.",
    solution: "Custom AI receptionist that books appointments, answers insurance questions, and handles emergency triage — 24/7.",
    result: "40+ qualified appointment leads captured in the first 30 days — most outside office hours.",
    icon: <Clock size={20} />,
    metric: "24/7",
    metricLabel: "Booking coverage",
  },
  {
    id: 2,
    business: "Summit Realty",
    industry: "Real Estate",
    challenge: "Agents wasting hours on unqualified buyer inquiries. Hot leads buried under cold ones.",
    solution: "Lead-qualification chatbot that pre-screens budget, timeline, and pre-approval status before routing to a human agent.",
    result: "Average lead qualification time dropped from 2 days to 60 seconds. Agents focus on closers.",
    icon: <Users size={20} />,
    metric: "60s",
    metricLabel: "Lead qualification",
  },
  {
    id: 3,
    business: "Velvet & Vine",
    industry: "E-Commerce",
    challenge: "Shoppers abandoning carts at checkout. Generic chatbots felt robotic and pushed customers away.",
    solution: "On-brand style assistant that helps customers find the right piece, answers order questions, and recovers carts with personalized conversation.",
    result: "18% lift in cart recovery. Customers describe the bot as 'like having a stylist.'",
    icon: <TrendingUp size={20} />,
    metric: "+18%",
    metricLabel: "Cart recovery",
  },
];

export default function CaseStudies() {
  return (
    <section id="work" data-testid="case-studies-section" className="bg-cream-pale section-pad">
      <div className="container-edit">
        <div className="max-w-2xl mb-14">
          <div className="eyebrow mb-5">Selected Work</div>
          <h2 className="h-section text-plum-deepest mb-6">
            Real businesses. <span className="font-display-italic text-gold-deep">Real outcomes.</span>
          </h2>
          <p className="text-lg text-muted-plum leading-relaxed">
            Sample case studies showing how Melavate chatbots solve real business problems. Each
            of these has a working live demo above — click any of the cards in "Live Demos" to chat
            with the actual AI.
          </p>
          <div className="text-xs text-muted-plum mt-4 italic">
            Sample businesses; metrics illustrate the kind of outcomes well-built chatbots produce.
          </div>
        </div>

        <div className="space-y-6">
          {CASE_STUDIES.map((cs) => (
            <article
              key={cs.id}
              data-testid={`case-study-${cs.id}`}
              className="bg-cream rounded-2xl p-6 md:p-8 border border-plum/8 grid md:grid-cols-12 gap-8 items-start"
            >
              <div className="md:col-span-3">
                <div className="flex items-center gap-3 mb-3 text-plum">
                  {cs.icon}
                  <span className="eyebrow text-gold-deep">{cs.industry}</span>
                </div>
                <h3 className="font-display text-2xl text-plum-deepest leading-tight mb-3">
                  {cs.business}
                </h3>
                <div className="pt-4 border-t border-plum/10">
                  <div className="font-display text-4xl text-plum mb-1">{cs.metric}</div>
                  <div className="text-xs uppercase tracking-widest text-muted-plum">
                    {cs.metricLabel}
                  </div>
                </div>
              </div>

              <div className="md:col-span-9 grid sm:grid-cols-3 gap-5">
                <CaseBlock label="Challenge" text={cs.challenge} accent="rose" />
                <CaseBlock label="Solution" text={cs.solution} accent="plum" />
                <CaseBlock label="Result" text={cs.result} accent="gold" />
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function CaseBlock({ label, text, accent }) {
  const accents = {
    rose: "border-[#B05858] text-[#4a2a2a] bg-[rgba(176,88,88,0.04)]",
    plum: "border-plum text-plum-deepest bg-[rgba(74,25,66,0.04)]",
    gold: "border-gold text-[#5a4520] bg-[rgba(201,169,97,0.08)]",
  };
  const labelColor = {
    rose: "text-[#B05858]",
    plum: "text-plum",
    gold: "text-gold-deep",
  };
  return (
    <div className={`rounded-lg p-4 border-l-[3px] ${accents[accent]}`}>
      <div className={`text-[0.65rem] uppercase tracking-[0.18em] font-bold mb-2 ${labelColor[accent]}`}>
        {label}
      </div>
      <p className="text-sm leading-relaxed font-body">{text}</p>
    </div>
  );
}
